import React, { useState, useMemo } from 'react';
import { Row, Col } from 'antd';
import Svg from 'react-inlinesvg';
import { signageIcon, storeIcon, contactIcon, rocketIcon } from '../icons';
import { useHistory, useLocation } from 'react-router-dom';
import { Settings, Activity, HelpCircle, Eye } from 'react-feather';
import MenuItems from '../MenuItems';
import DeactivateStoreLink from 'pages/settings/DeactivateStoreLink';
import EditStore from 'components/Dashboard/Store/StoreModal/Actions/EditStore';
import { useStoreDetails } from 'api/store';
import DuplicateStore from 'components/Dashboard/Store/StoreModal/Actions/DuplicateStore';
import { getStoreList, useOwnerDetails } from 'api/owners';
import { Spinner } from 'ui-kit';
import { PaymentPlan } from 'api/types/owner';
import ActiveStore from 'components/StoreList/ActiveStore';
import { useSignedUser } from 'api/auth';
import { reverse } from 'utils/urls';
import { URLS } from 'components/Routes/urls';

const MODAL_KEYS = {
  duplicate: 0,
  edit: 1,
};

interface IProps {
  store: any;
  reset: boolean;
  initialActiveTab: string;
  mode: 'inline' | 'horizontal';
}

const TabStore = ({ mode, store, reset, initialActiveTab }: IProps) => {
  const [openModalKey, setOpenModalKey] = useState<undefined | number>(undefined);
  const history = useHistory();
  const location = useLocation();

  const { data: user } = useSignedUser();
  const { data, isLoading, invalidate: mutateOwnerDetails } = useOwnerDetails(user?.owner_id);
  const { plan } = data || {};
  const { mutate: mutateStoreData } = useStoreDetails(store?.id, store?.id !== null);
  const { invalidate: mutateStoreList } = getStoreList(user?.owner_id);

  const getUpgradeString = (plan) => {
    switch (plan) {
      case 'BASIC':
        return 'Upgrade to Pro';
      case 'PRO':
        return 'Upgrade to Pro Plus';
      case 'PRO_PLUS':
        return 'Upgrade to Enterprise';
      default:
        return '';
    }
  };

  const handleOpenModalKey = (key) => {
    setOpenModalKey(key);
  };

  const handleCloseModalKey = () => {
    setOpenModalKey(undefined);
  };

  const handleStoreArchive = () => {
    history.push(URLS.STORES);
    mutateStoreList();
  };

  const handleStoreInactive = () => {
    onUpdateStore();
  };

  const onUpdateStore = () => {
    if (location.pathname === URLS.STORES) {
      mutateStoreList();
      mutateOwnerDetails();
    } else {
      mutateStoreData();
    }
    handleCloseModalKey();
  };

  const STORE_MENU_ITEMS = useMemo(
    () => [
      {
        key: 'store',
        label: 'Store',
        icon: <Svg src={storeIcon} />,
        onClick: () =>
          history.push(
            reverse({
              url: URLS.STORE_DETAILS,
              params: { storeId: store.id },
            }),
          ),
      },
      {
        key: 'signage',
        label: 'Signage',
        icon: <Svg src={signageIcon} />,
        onClick: () =>
          history.push(
            reverse({
              url: URLS.STORE_SIGNAGE,
              params: { storeId: store.id },
            }),
          ),
      },
      {
        key: 'sales',
        label: 'Sales',
        icon: <Activity />,
        onClick: () =>
          history.push(
            reverse({
              url: URLS.STORE_SALES_LIST,
              params: { storeId: store.id },
            }),
          ),
      },
      {
        key: 'manage',
        label: 'Manage',
        type: 'submenu',
        icon: <Settings />,
        items: [
          {
            key: 'edit',
            label: 'Edit',
            onClick: () => handleOpenModalKey(MODAL_KEYS.edit),
          },
          {
            key: 'duplicate',
            label: 'Duplicate',
            onClick: () => handleOpenModalKey(MODAL_KEYS.duplicate),
          },
          {
            key: 'archive',
            label: (
              <DeactivateStoreLink
                storeId={store?.id}
                onChange={handleStoreArchive}
                status={store?.isActive}
                color="textDark"
              />
            ),
          },
          {
            key: 'store-activity',
            label: (
              <ActiveStore
                inSideBar
                isActive={store?.isVisible}
                storeId={store?.id}
                storeName={store?.storeName}
                onUpdate={handleStoreInactive}
              />
            ),
          },
        ],
      },
      {
        key: 'divider-1',
        type: 'divider',
      },
      {
        key: 'support-submenu-store',
        label: 'Support',
        type: 'submenu',
        icon: <HelpCircle />,
        items: [
          {
            key: 'Best Practices',
            label: 'Best Practices',
            icon: <HelpCircle />,
            onClick: () => {
              window.open(
                'https://thehostcohelp.zendesk.com/hc/en-us/categories/15225750829325-Best-Practices',
                '_blank',
              );
            },
          },
          {
            key: 'explore-store',
            label: 'Example store',
            icon: <Eye />,
            onClick: () => {
              window.open('https://app.thehost.co/store/19/', '_blank');
            },
          },
          {
            key: 'contact-us',
            label: 'Contact us',
            icon: <Svg src={contactIcon} />,
            onClick: () => {
              window.open('https://www.thehost.co/contact', '_blank');
            },
          },
        ],
      },
      {
        key: 'pro-upgrade-link',
        label: getUpgradeString(plan),
        hidden: plan === PaymentPlan.Enterprise,
        icon: <Svg src={rocketIcon} />,
        onClick: () => {
          history.push('/pricing/');
        },
        className: 'menu-item-pro-upgrade',
      },
    ],
    [
      store,
      plan,
      history,
      handleStoreInactive,
      handleOpenModalKey,
      getUpgradeString,
      onUpdateStore,
    ],
  );

  if (isLoading) {
    return <Spinner />;
  }
  return (
    <Row>
      <Col xs={24}>
        <MenuItems
          mode={mode}
          items={STORE_MENU_ITEMS}
          reset={reset}
          initialActiveTab={initialActiveTab}
        />
      </Col>
      {store && (
        <>
          <DuplicateStore
            isOpen={openModalKey === MODAL_KEYS.duplicate}
            store={store}
            onCancel={handleCloseModalKey}
            onSuccess={(store) =>
              history.push(
                reverse({
                  url: URLS.STORE_DETAILS,
                  params: { storeId: store.id },
                }),
              )
            }
          />
          <EditStore
            isOpen={openModalKey === MODAL_KEYS.edit}
            store={store}
            onCancel={handleCloseModalKey}
            onSuccess={onUpdateStore}
          />
        </>
      )}
    </Row>
  );
};

export default TabStore;
